/**
 * Storage key used by Volto < 18.30 (before plone/volto#7925) to persist the
 * whole blocksClipboard reducer. The current store persists the dotted keys
 * `<LEGACY_BLOCKS_CLIPBOARD_KEY>.cut` / `.copy` instead and never touches the
 * legacy key again, so stale copies survive there indefinitely (#1707).
 */
export const LEGACY_BLOCKS_CLIPBOARD_KEY =
  'redux_localstorage_simple_blocksClipboard';

type ClipboardEntries = [string, Record<string, unknown>][];

export interface BlocksClipboard {
  cut?: ClipboardEntries;
  copy?: ClipboardEntries;
}

const readStorageKey = (key: string): ClipboardEntries | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    // localStorage can throw (privacy mode, disabled storage) and stored
    // values may be unparseable; treat both as "nothing persisted".
    return undefined;
  }
};

/**
 * Reads the blocks clipboard from localStorage using the same dotted keys the
 * Redux store persists (config.settings.persistentReducers via
 * redux-localstorage-simple: namespace + '_' + state path).
 */
export function loadBlocksClipboardFromStorage(): BlocksClipboard {
  const cut = readStorageKey(`${LEGACY_BLOCKS_CLIPBOARD_KEY}.cut`);
  const copy = readStorageKey(`${LEGACY_BLOCKS_CLIPBOARD_KEY}.copy`);

  return {
    ...(cut ? { cut } : {}),
    ...(copy ? { copy } : {}),
  };
}

/** The two keys the store actually persists the clipboard under. */
export const BLOCKS_CLIPBOARD_KEYS: Record<string, keyof BlocksClipboard> = {
  [`${LEGACY_BLOCKS_CLIPBOARD_KEY}.cut`]: 'cut',
  [`${LEGACY_BLOCKS_CLIPBOARD_KEY}.copy`]: 'copy',
};

/**
 * How long a locally made copy/cut lives in memory only: the store persists
 * with `save({ …, debounce: 500 })` (volto/src/store.js:29). Storage events
 * arriving inside that window describe an OLDER state than ours.
 */
export const BLOCKS_CLIPBOARD_PERSIST_DEBOUNCE_MS = 500;

/**
 * Derives the next clipboard state from a `storage` event, or `null` when the
 * event must not touch the clipboard at all (#1728, follow-up to !1466).
 *
 * Upstream reloaded the whole clipboard from localStorage on every same-origin
 * storage event. Two things make that wrong:
 *
 * 1. The event fires for *every* key — analytics scripts and unrelated tabs
 *    included — so an unrelated write replaced the clipboard.
 * 2. Re-reading *both* keys resurrects whatever else sits in storage. A stale
 *    `.cut` from an earlier session would then join a fresh `.copy`, and
 *    `pasteBlocks` prefers `cut` whenever it is present
 *    (BlocksToolbar.pasteBlocks) — so the editor pastes old blocks.
 *
 * Hence: only clipboard keys are considered, and the changed entry is taken
 * from the event itself and adopted as the whole clipboard — mirroring the
 * reducer, which holds either a cut or a copy, never both.
 *
 * A `null` key means another tab called `localStorage.clear()`. We ignore it:
 * a stale-but-present clipboard is a far smaller problem than silently
 * dropping the copy the editor just made.
 */
export function applyBlocksClipboardStorageEvent(
  current: BlocksClipboard,
  event: Pick<StorageEvent, 'key' | 'newValue'>,
): BlocksClipboard | null {
  const mode = event.key ? BLOCKS_CLIPBOARD_KEYS[event.key] : undefined;
  if (!mode) {
    return null;
  }

  if (event.newValue === null || event.newValue === undefined) {
    return {};
  }

  try {
    return { [mode]: JSON.parse(event.newValue) };
  } catch {
    // Unparseable payload — leave the in-memory clipboard as it is.
    return null;
  }
}

/**
 * Removes the legacy clipboard entry so pre-upgrade copies can no longer
 * resurface via the storage-event sync in the blocks toolbar.
 */
export function removeLegacyBlocksClipboard(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(LEGACY_BLOCKS_CLIPBOARD_KEY);
  } catch {
    // Best-effort cleanup; must never break the toolbar.
  }
}
